import {
  Dispatch,
  MouseEvent,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import ImageNotify from '@/assets/img/notify-img.svg';
import AvatarDashboard from '@/components/AvatarDashboard';
import CreateInstitutionalPage from '@/components/InstitutionalPage/CreateInstitutionalPage/InviteCreateInstitutionalPage';
import ConfigureCompanyInviteModal from '@/components/Modals/ConfigureCompanyInviteModal';
import { LogoutModal } from '@/components/Modals/LogoutModal';
import ModalDefault from '@/components/Modals/ModalDefault';
import MyAccountModal from '@/components/Modals/MyAccount';
import MyCompany from '@/components/Modals/MyCompany';
import RequestAccess from '@/components/Modals/UserGestation/RequestAccess';
import ProgressBar from '@/components/ProgressBar';
import { useUser } from '@/contexts/AuthContext';
import { useDrawerContext } from '@/contexts/DrawerContext';
import { Toast } from '@/lib/Toast';
import { themeLight } from '@/themes/light';
import { IVisitor } from '@/types';
import {
  generateWhatsappUrl,
  hasParamsConfigureNotifications,
  hasRoutersHiddenSideBar,
  priorityUserName,
} from '@/utils';
import { ROUTER_PAGES, SUPPORT_WHATSAPP } from '@/utils/constants';
import { isAuthenticatedOrFirstAccessPage } from '@/utils/validates';
import {
  ArrowForwardIos,
  AssignmentOutlined,
  DescriptionOutlined,
  HomeOutlined,
  InfoOutlined,
  PersonOutline,
  Storefront,
  StoreOutlined,
  Close,
  CategoryOutlined,
  WhatsApp,
} from '@mui/icons-material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  SxProps,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

type SideMenuProps = {
  children: ReactNode;
  visitorData?: IVisitor;
};

interface IDrawerStyleProps {
  isAuthenticatedValue: boolean;
}

const sideMenuAnimation = {
  transition: 'all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
};

const useStyles = makeStyles(() => ({
  textSideMenu: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    color: '#808080',
  },
  item: {
    padding: '0px',
    color: '#686868',
    cursor: 'pointer',
    display: 'flex',
    width: 'clamp(240px, 100%, 310px)',
    gap: '42px',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
}));

const DrawerStyle = styled(Drawer, {
  shouldForwardProp: prop => prop !== 'isAuthenticatedValue',
})<IDrawerStyleProps>(({ open, isAuthenticatedValue }) => ({
  '& .MuiPaper-root': {
    borderRadius: '0 12px 0 0',
  },
  '& .MuiDrawer-paper': {
    overflow: 'hidden',
    border: 'none',
    width: '320px',
    height: '100vh',
    zIndex: 998,
    ...sideMenuAnimation,
  },
  [themeLight.breakpoints.up('md')]: {
    ...(open && {
      '& .MuiDrawer-paper': {
        width: '310px',
      },
    }),
    ...(!open && isAuthenticatedValue
      ? {
          '& .MuiDrawer-paper': {
            width: '56px',
            position: 'fixed',
          },
        }
      : !open && {
          '& .MuiDrawer-paper': {
            width: '0px',
          },
        }),
  },
}));

interface ISideMenuItem {
  onClick?: () => void;
  icon: JSX.Element;
  text: string;
  textColor?: boolean;
  sx?: SxProps;
  textTooltip: string;
  isDrawerOpenValue: boolean;
  showIconNew?: boolean;
}

const SideMenuItem = ({
  onClick,
  icon,
  text,
  textColor,
  sx,
  textTooltip,
  isDrawerOpenValue,
  showIconNew,
}: ISideMenuItem) => {
  const classes = useStyles();

  return (
    <ListItem className={classes.item} onClick={onClick} sx={sx}>
      <Tooltip
        disableHoverListener={isDrawerOpenValue}
        title={textTooltip}
        arrow
        placement="right"
      >
        <ListItemIcon sx={{ minWidth: 'auto', padding: '14px 15px' }}>
          <Badge
            color="primary"
            overlap="circular"
            badgeContent=" "
            variant="dot"
            invisible={(isDrawerOpenValue && showIconNew) || !showIconNew}
          >
            {icon}
          </Badge>
        </ListItemIcon>
      </Tooltip>
      <ListItemText className={classes.textSideMenu}>
        <Typography variant="body2" color={textColor ? '#00A8F3' : ''}>
          {text}
        </Typography>
      </ListItemText>
      {showIconNew && (
        <Box
          sx={{
            position: 'absolute',
            right: '20px',
            backgroundColor: 'primary.main',
            px: '8px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" color="primary.contrastText">
            NOVO
          </Typography>
        </Box>
      )}
    </ListItem>
  );
};

export const SideMenu = ({ children, visitorData }: SideMenuProps) => {
  const mdUp = useMediaQuery(themeLight.breakpoints.up('md'));
  const router = useRouter();
  const { configure_notifications, edit_company } = router.query;

  const { user, progressRatePercentage, defaultMembership, isAuthenticated } =
    useUser();
  const { isDrawerOpen, toggleDrawerOpen } = useDrawerContext();

  const [isOpenModalLogout, setIsOpenModalLogout] = useState(false);
  const [isModalMyAccountOpen, setIsModalMyAccountOpen] = useState(
    hasParamsConfigureNotifications(configure_notifications)
  );
  const [openCreatePage, setOpenCreatePage] = useState(false);
  const [openRequestAccess, setOpenRequestAccess] = useState(false);
  const [openConfigureCompany, setOpenConfigureCompany] = useState(false);
  const [openValidateMembership, setOpenValidateMembership] = useState(false);
  const [openValidateCpf, setOpenValidateCpf] = useState(false);
  const [openMyCompany, setOpenMyCompany] = useState(edit_company === 'true');

  const isShowSideMenu = !hasRoutersHiddenSideBar(router, isAuthenticated);

  const isDesktopOpen = mdUp && isDrawerOpen;

  const hasCompanyName = defaultMembership
    ? defaultMembership.company.short_name
    : visitorData?.company_name;
  const identificationName = visitorData?.name || priorityUserName(user);
  const isOneHundredPercent = progressRatePercentage === 100;

  const ifIsUser = (fn: () => void) => {
    if (isDrawerOpen) toggleDrawerOpen();

    if (visitorData?.id) {
      Toast('error', 'Para habilitar essa função, primeiro defina sua senha!');
      return;
    }

    fn();
  };

  const ifAlreadyValidateEmail = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isDrawerOpen) toggleDrawerOpen();

    if (visitorData?.id)
      return actAndPreventDefault(event, () =>
        Toast('error', 'Para habilitar essa função, primeiro defina sua senha!')
      );

    if (!user.verified_email)
      return actAndPreventDefault(event, () =>
        Toast('error', 'Você deve validar o email primeiro!')
      );
  };

  const handleClickInstitutionalPage = (
    event: MouseEvent<HTMLAnchorElement>
  ) => {
    if (isDrawerOpen) toggleDrawerOpen();

    const institutionalPageIsActive =
      defaultMembership?.company.has_active_institutional_page;

    if (institutionalPageIsActive) return;

    const companyHasSuperadmins = defaultMembership?.company.has_superadmins;

    const strongMemberCanCreate =
      defaultMembership?.is_validated &&
      defaultMembership?.has_confirmed_configuration;

    const canCreateInstitutionalPage =
      defaultMembership?.is_superadmin || strongMemberCanCreate;

    const strongMemberCanRequestAccess =
      companyHasSuperadmins &&
      !defaultMembership.is_superadmin &&
      defaultMembership.is_validated;

    if (!user.verified_email) return ifAlreadyValidateEmail(event);

    if (!user.verified_cpf)
      return actAndPreventDefault(event, () => setOpenValidateCpf(true));

    if (!defaultMembership?.company.has_confirmed_configuration)
      return actAndPreventDefault(event, () => setOpenConfigureCompany(true));

    if (!defaultMembership?.is_validated)
      return actAndPreventDefault(event, () => setOpenValidateMembership(true));

    if (canCreateInstitutionalPage)
      return actAndPreventDefault(event, () => setOpenCreatePage(true));

    if (strongMemberCanRequestAccess)
      return actAndPreventDefault(event, () => setOpenRequestAccess(true));
  };

  function handleClickPriceWindow(event: MouseEvent<HTMLAnchorElement>) {
    if (isDrawerOpen) toggleDrawerOpen();

    if (!user.verified_email) return ifAlreadyValidateEmail(event);

    if (!user.verified_cpf)
      return actAndPreventDefault(event, () => setOpenValidateCpf(true));

    if (!defaultMembership?.company.has_confirmed_configuration)
      return actAndPreventDefault(event, () => setOpenConfigureCompany(true));

    if (!defaultMembership?.is_validated)
      return actAndPreventDefault(event, () => setOpenValidateMembership(true));
  }

  function actAndPreventDefault(
    event: MouseEvent<HTMLAnchorElement>,
    fn: Function
  ) {
    event.preventDefault();
    fn();
  }

  function closeModalAndSetRouter(
    setModalState: Dispatch<SetStateAction<boolean>>,
    query: string
  ) {
    setModalState(false);
    router.push({
      pathname: ROUTER_PAGES.home,
      query: { [query]: true },
    });
  }

  function handleClickMyCompany() {
    if (isDrawerOpen) {
      toggleDrawerOpen();
    }

    setOpenMyCompany(true);
  }

  useEffect(() => {
    if (defaultMembership?.company && edit_company === 'true') {
      setOpenMyCompany(edit_company === 'true');
    }
  }, [defaultMembership?.company, edit_company]);

  return (
    <>
      {isShowSideMenu && (
        <>
          <DrawerStyle
            isAuthenticatedValue={isAuthenticated}
            open={isDrawerOpen}
            variant={mdUp ? 'permanent' : 'temporary'}
            onClose={toggleDrawerOpen}
            sx={{
              '& .MuiDrawer-paper': {
                height: '100%',
              },
            }}
          >
            {isAuthenticatedOrFirstAccessPage(isAuthenticated, router) ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                {!mdUp && (
                  <Close
                    sx={{
                      cursor: 'pointer',
                      position: 'absolute',
                      right: 16,
                      top: 16,
                    }}
                    onClick={toggleDrawerOpen}
                  />
                )}
                <Box
                  sx={{
                    p: 2,
                    overflow: 'hidden',
                    width: '310px',
                    height:
                      !isDrawerOpen && mdUp
                        ? '66px'
                        : mdUp && isOneHundredPercent
                        ? '96px'
                        : '152px',
                    ...sideMenuAnimation,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: mdUp ? 'center' : 'flex-start',
                      flexDirection: mdUp ? 'row' : 'column',
                      gap: '12px',
                      marginBottom: '12px',
                    }}
                  >
                    <AvatarDashboard hiddenSmallAvatar={mdUp} />
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                      >
                        {hasCompanyName ? hasCompanyName : identificationName}
                      </Typography>
                      <Typography variant="caption">
                        {mdUp && hasCompanyName
                          ? identificationName
                          : 'Fornecedor'}
                      </Typography>
                    </Box>
                  </Box>
                  {mdUp && !isOneHundredPercent && (
                    <ProgressBar
                      progressNumber={progressRatePercentage}
                      backgroundColor="#F4F4F4"
                      colorText="#ACACAC"
                    />
                  )}
                </Box>
                <List
                  sx={theme => ({
                    paddingY: '0',
                    px: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    [theme.breakpoints.up('sm')]: {
                      px: 0,
                    },
                  })}
                >
                  <Link href={ROUTER_PAGES.home} passHref>
                    <a
                      data-testid="anchor-home"
                      onClick={ifAlreadyValidateEmail}
                      style={{
                        backgroundColor:
                          ROUTER_PAGES.home === router.asPath ? '#d9f2fd' : '',
                        borderRadius: '4px',
                      }}
                    >
                      <SideMenuItem
                        icon={
                          <HomeOutlined
                            sx={{
                              color:
                                ROUTER_PAGES.home === router.asPath
                                  ? '#00A8F3'
                                  : '',
                            }}
                          />
                        }
                        textColor={ROUTER_PAGES.home === router.asPath}
                        text="Início"
                        textTooltip="Início"
                        isDrawerOpenValue={isDrawerOpen}
                      />
                    </a>
                  </Link>
                  <Link
                    href="/[slug]"
                    as={ROUTER_PAGES.respostaCotacoes.index}
                    passHref
                  >
                    <a
                      data-testid="anchor-cotacoes"
                      onClick={ifAlreadyValidateEmail}
                      style={{
                        backgroundColor:
                          ROUTER_PAGES.respostaCotacoes.index === router.asPath
                            ? '#d9f2fd'
                            : '',
                        borderRadius: '4px',
                      }}
                    >
                      <SideMenuItem
                        icon={
                          <AssignmentOutlined
                            sx={{
                              color:
                                ROUTER_PAGES.respostaCotacoes.index ===
                                router.asPath
                                  ? '#00A8F3'
                                  : '',
                            }}
                          />
                        }
                        textColor={
                          ROUTER_PAGES.respostaCotacoes.index === router.asPath
                        }
                        text="Cotações"
                        textTooltip="Cotações"
                        isDrawerOpenValue={isDrawerOpen}
                      />
                    </a>
                  </Link>
                  {defaultMembership?.is_superadmin && (
                    <Link href="/[slug]" as={ROUTER_PAGES.usuarios} passHref>
                      <a
                        data-testid="anchor-user-gestation"
                        onClick={ifAlreadyValidateEmail}
                        style={{
                          backgroundColor:
                            ROUTER_PAGES.usuarios === router.asPath
                              ? '#d9f2fd'
                              : '',
                          borderRadius: '4px',
                        }}
                      >
                        <SideMenuItem
                          icon={
                            <AccountCircleOutlinedIcon
                              sx={{
                                color:
                                  ROUTER_PAGES.usuarios === router.asPath
                                    ? '#00A8F3'
                                    : '',
                              }}
                            />
                          }
                          textColor={ROUTER_PAGES.usuarios === router.asPath}
                          text="Gestão de usuários"
                          textTooltip="Gestão de usuários"
                          isDrawerOpenValue={isDrawerOpen}
                        />
                      </a>
                    </Link>
                  )}
                  <Link
                    href="/[slug]"
                    as={ROUTER_PAGES.ordensDeCompra}
                    passHref
                  >
                    <a
                      data-testid="anchor-orders"
                      onClick={ifAlreadyValidateEmail}
                      style={{
                        backgroundColor:
                          ROUTER_PAGES.ordensDeCompra === router.asPath
                            ? '#d9f2fd'
                            : '',
                        borderRadius: '4px',
                      }}
                    >
                      <SideMenuItem
                        icon={
                          <DescriptionOutlined
                            sx={{
                              color:
                                ROUTER_PAGES.ordensDeCompra === router.asPath
                                  ? '#00A8F3'
                                  : '',
                            }}
                          />
                        }
                        textColor={
                          ROUTER_PAGES.ordensDeCompra === router.asPath
                        }
                        text="Ordens de compra"
                        textTooltip="Ordens de compra"
                        isDrawerOpenValue={isDrawerOpen}
                      />
                    </a>
                  </Link>

                  <Link href={ROUTER_PAGES.vitrinePreco} passHref>
                    <a
                      data-testid="anchor-price-window"
                      onClick={handleClickPriceWindow}
                      style={{
                        backgroundColor:
                          ROUTER_PAGES.vitrinePreco === router.asPath
                            ? '#d9f2fd'
                            : '',
                        borderRadius: '4px',
                      }}
                    >
                      <SideMenuItem
                        icon={
                          <CategoryOutlined
                            sx={{
                              color:
                                ROUTER_PAGES.vitrinePreco === router.asPath
                                  ? '#00A8F3'
                                  : '',
                            }}
                          />
                        }
                        textColor={ROUTER_PAGES.vitrinePreco === router.asPath}
                        text="Vitrine de preços"
                        textTooltip="Vitrine de preços"
                        isDrawerOpenValue={isDrawerOpen}
                        showIconNew
                      />
                    </a>
                  </Link>

                  <Link
                    href={`${ROUTER_PAGES.site}/${defaultMembership?.company.slug}`}
                    passHref
                  >
                    <a
                      data-testid="anchor-institutional-page"
                      onClick={handleClickInstitutionalPage}
                      style={{
                        backgroundColor:
                          `${ROUTER_PAGES.site}/${defaultMembership?.company.slug}` ===
                          router.asPath
                            ? '#d9f2fd'
                            : '',
                        borderRadius: '4px',
                      }}
                    >
                      <SideMenuItem
                        icon={
                          <Storefront
                            sx={{
                              color:
                                `${ROUTER_PAGES.site}/${defaultMembership?.company.slug}` ===
                                router.asPath
                                  ? '#00A8F3'
                                  : '',
                            }}
                          />
                        }
                        textColor={
                          `${ROUTER_PAGES.site}/${defaultMembership?.company.slug}` ===
                          router.asPath
                        }
                        text="Página institucional"
                        textTooltip="Página institucional"
                        isDrawerOpenValue={isDrawerOpen}
                      />
                    </a>
                  </Link>

                  {defaultMembership && (
                    <SideMenuItem
                      icon={<StoreOutlined />}
                      text="Minha empresa"
                      textTooltip="Minha empresa"
                      onClick={handleClickMyCompany}
                      isDrawerOpenValue={isDrawerOpen}
                    />
                  )}

                  <Link href={ROUTER_PAGES.obra_play} passHref>
                    <a target="_blank" rel="noreferrer">
                      <SideMenuItem
                        icon={<InfoOutlined />}
                        text="Conheça o Obra Play"
                        textTooltip="Conheça o Obra Play"
                        isDrawerOpenValue={isDrawerOpen}
                      />
                    </a>
                  </Link>

                  <Link href={generateWhatsappUrl(SUPPORT_WHATSAPP)} passHref>
                    <a
                      data-testid="anchor-talk-team"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <SideMenuItem
                        icon={<WhatsApp />}
                        text="Fale com nosso time"
                        textTooltip="Fale com nosso time"
                        isDrawerOpenValue={isDrawerOpen}
                      />
                    </a>
                  </Link>

                  <Box sx={{ marginTop: 'auto' }}>
                    <SideMenuItem
                      icon={<ExitToAppIcon />}
                      text="Sair"
                      onClick={() => setIsOpenModalLogout(true)}
                      textTooltip="Sair"
                      isDrawerOpenValue={isDrawerOpen}
                    />
                  </Box>
                </List>

                <Box
                  sx={{
                    width: 'clamp(256px, 100%, 310px)',
                  }}
                >
                  <Divider />
                  <Grid
                    container
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px calc((56px - 30px)/2)',
                      cursor: 'pointer',
                      ...sideMenuAnimation,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                    onClick={() =>
                      ifIsUser(() => setIsModalMyAccountOpen(true))
                    }
                  >
                    <Avatar
                      data-testid="mini-avatar-img"
                      src={visitorData ? '' : user?.avatar}
                      sx={{
                        width: 30,
                        height: 30,
                        backgroundColor: '#E9E9E9',
                        color: '#C7C7C7',
                        border: '1px solid currentColor',
                      }}
                    >
                      <PersonOutline sx={{ fontSize: '18px' }} />
                    </Avatar>
                    {isDrawerOpen && (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            ml: '24px',
                            whiteSpace: 'nowrap',
                            width: '20ch',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                          data-testid="user-identification"
                        >
                          {identificationName}
                        </Typography>
                        <ArrowForwardIos
                          sx={{ fontSize: '12px', marginLeft: 'auto' }}
                        />
                      </>
                    )}
                  </Grid>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  gap: 2,
                  m: 2,
                }}
              >
                <Box sx={{ width: '100%', textAlign: 'end' }}>
                  <Close
                    sx={{ cursor: 'pointer' }}
                    onClick={toggleDrawerOpen}
                  />
                </Box>
                <Button variant="outlined" href={ROUTER_PAGES.login()}>
                  entrar
                </Button>
                <Button variant="contained" href={ROUTER_PAGES.cadastro}>
                  cadastre-se
                </Button>
              </Box>
            )}
          </DrawerStyle>
        </>
      )}

      <Box
        sx={theme => ({
          ...sideMenuAnimation,
          [theme.breakpoints.up('md')]: {
            marginLeft:
              mdUp && isDesktopOpen && isShowSideMenu
                ? '256px'
                : isAuthenticated && isShowSideMenu
                ? '56px'
                : '',
          },
        })}
      >
        {children}
      </Box>

      <MyAccountModal
        isOpen={isModalMyAccountOpen}
        onClose={() => setIsModalMyAccountOpen(false)}
      />

      <LogoutModal
        isOpen={isOpenModalLogout}
        onClose={() => setIsOpenModalLogout(false)}
      />

      <RequestAccess
        handleClose={() => setOpenRequestAccess(false)}
        isOpen={openRequestAccess}
      />

      <ConfigureCompanyInviteModal
        isOpen={openConfigureCompany}
        onClose={() => setOpenConfigureCompany(false)}
        onClickButton={() => {
          closeModalAndSetRouter(setOpenConfigureCompany, 'configure_company');
        }}
      />

      <ModalDefault
        src={ImageNotify}
        alt="Imagem representando aviso"
        isOpen={openValidateMembership}
        onClose={() => setOpenValidateMembership(false)}
        title="Atenção fornecedor!"
        subtitle="Você precisa validar seu vínculo com a empresa primeiro."
        textButton="VALIDAR"
        onClickButton={() => {
          closeModalAndSetRouter(setOpenValidateMembership, 'validate_cnpj');
        }}
      />

      <ModalDefault
        src={ImageNotify}
        alt="Imagem representando aviso"
        isOpen={openValidateCpf}
        onClose={() => setOpenValidateCpf(false)}
        title="Atenção fornecedor!"
        subtitle="Você precisa validar seu CPF primeiro!"
        textButton="VALIDAR DADOS PESSOAIS"
        onClickButton={() => {
          closeModalAndSetRouter(setOpenValidateCpf, 'validate_cpf');
        }}
      />

      <CreateInstitutionalPage
        isOpen={openCreatePage}
        handleClose={() => setOpenCreatePage(false)}
      />

      <MyCompany
        isOpen={openMyCompany}
        handleClose={() => setOpenMyCompany(false)}
      />
    </>
  );
};

export { sideMenuAnimation };
export type { SideMenuProps };
